/**
 * AssistantsWindow — self-contained AI assistant chat panel.
 *
 * Features:
 *   - One tab per mode with independent conversation state
 *   - Streaming SSE via fetch + ReadableStream
 *   - Markdown rendering (accepts an optional renderMarkdown prop)
 *   - Copy button per assistant message
 *   - Thumbs up / down (human_review: 1.0 / 0.0)
 *   - New conversation clears the active tab only
 */

import {
  Button,
  Spinner,
  Tab,
  TabList,
  Text,
  Textarea,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  CopyRegular,
  DismissRegular,
  SendRegular,
  ThumbDislikeRegular,
  ThumbLikeRegular,
} from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { parseSSEStream } from "../utils/parseSSEStream";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantMode {
  assistantId: string;
  name: string;
  avatarUrl?: string;
  placeholder?: string;
  /** Human-readable name of the page where the chat is opened, e.g. "Collections". */
  pageContext?: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Server-assigned id used for reactions. */
  messageId?: string;
}

interface TabState {
  messages: Message[];
  input: string;
  streaming: boolean;
}

interface ChatStreamEvent {
  content?: string;
  done?: boolean;
  messageId?: string;
}

// ── Internal API helpers (inline — no api-client dep to avoid circular refs) ──

async function _streamChat(
  payload: { session_id: string; assistant_id: string; message: string; page_context?: string },
  onEvent: (event: ChatStreamEvent) => void,
): Promise<void> {
  const r = await fetch("/api/v1/assistants/chat/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!r.ok) throw new Error(`Chat stream error: ${r.status}`);

  if (!r.body) return;
  await parseSSEStream<ChatStreamEvent>(r.body, onEvent);
}

async function _postReaction(message_id: string, human_review: number): Promise<void> {
  await fetch("/api/v1/assistants/chat/reactions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message_id, human_review }),
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    bottom: "96px",
    right: "24px",
    width: "420px",
    maxWidth: "calc(100vw - 48px)",
    height: "600px",
    maxHeight: "calc(100vh - 120px)",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow64,
    borderRadius: tokens.borderRadiusXLarge,
    overflow: "hidden",
    zIndex: 999,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  tabList: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: `0 ${tokens.spacingHorizontalM}`,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingHorizontalM,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  messageBubble: {
    maxWidth: "85%",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: "break-word",
  },
  userBubble: {
    alignSelf: "flex-end",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  assistantBubble: {
    alignSelf: "flex-start",
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  messageActions: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
    alignSelf: "flex-start",
  },
  inputRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    alignItems: "flex-end",
  },
  toolbar: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

let _msgCounter = 0;
const newId = () => `msg-${Date.now()}-${++_msgCounter}`;

const emptyTab = (): TabState => ({ messages: [], input: "", streaming: false });

// ── Component ─────────────────────────────────────────────────────────────────

export interface AssistantsWindowProps {
  modes: AssistantMode[];
  onClose: () => void;
  /**
   * Optional markdown renderer. If not provided, content is rendered as
   * preformatted text. Pass react-markdown or similar from the consuming app.
   */
  renderMarkdown?: (content: string) => React.ReactNode;
}

export function AssistantsWindow({
  modes,
  onClose,
  renderMarkdown,
}: AssistantsWindowProps): React.JSX.Element {
  const styles = useStyles();

  const [activeTab, setActiveTab] = useState(modes[0]?.assistantId ?? "");
  const [tabState, setTabState] = useState<Record<string, TabState>>(
    () => Object.fromEntries(modes.map((m) => [m.assistantId, emptyTab()])),
  );

  const sessionId = useRef(`session-${Date.now()}`);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const currentMode = modes.find((m) => m.assistantId === activeTab);
  const current = tabState[activeTab] ?? emptyTab();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [tabState, activeTab]);

  // ── Tab state helpers ─────────────────────────────────────────────────────

  const updateTab = (id: string, patch: Partial<TabState>) =>
    setTabState((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyTab()), ...patch } }));

  const appendMessage = (tabId: string, msg: Message) =>
    setTabState((prev) => ({
      ...prev,
      [tabId]: { ...(prev[tabId] ?? emptyTab()), messages: [...(prev[tabId]?.messages ?? []), msg] },
    }));

  const updateLastAssistantContent = (tabId: string, content: string) =>
    setTabState((prev) => {
      const msgs = [...(prev[tabId]?.messages ?? [])];
      const last = msgs[msgs.length - 1];
      if (last?.role === "assistant") msgs[msgs.length - 1] = { ...last, content };
      return { ...prev, [tabId]: { ...(prev[tabId] ?? emptyTab()), messages: msgs } };
    });

  const setServerMessageId = (tabId: string, localId: string, serverId: string) =>
    setTabState((prev) => ({
      ...prev,
      [tabId]: {
        ...(prev[tabId] ?? emptyTab()),
        messages: (prev[tabId]?.messages ?? []).map((m) =>
          m.id === localId ? { ...m, messageId: serverId } : m,
        ),
      },
    }));

  // ── Send ──────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    if (!currentMode || !current.input.trim() || current.streaming) return;

    const message = current.input.trim();
    const tabId = activeTab;
    const userMsgId = newId();
    const assistantMsgId = newId();

    appendMessage(tabId, { id: userMsgId, role: "user", content: message });
    appendMessage(tabId, { id: assistantMsgId, role: "assistant", content: "" });
    updateTab(tabId, { input: "", streaming: true });

    let accumulated = "";

    try {
      await _streamChat(
        { session_id: sessionId.current, assistant_id: currentMode.assistantId, message, page_context: currentMode.pageContext },
        (event) => {
          if (event.content !== undefined) {
            accumulated += event.content;
            updateLastAssistantContent(tabId, accumulated);
          }
          if (event.done && event.messageId) {
            setServerMessageId(tabId, assistantMsgId, event.messageId);
          }
        },
      );
    } catch (err) {
      updateLastAssistantContent(
        tabId,
        `Error: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      updateTab(tabId, { streaming: false });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // ── Reactions ────────────────────────────────────────────────────────────

  const handleReaction = (msg: Message, value: number) => {
    if (!msg.messageId) return;
    void _postReaction(msg.messageId, value);
  };

  const handleCopy = (content: string) => {
    void navigator.clipboard.writeText(content);
  };

  // ── New conversation ──────────────────────────────────────────────────────

  const handleNewConversation = () => {
    sessionId.current = `session-${Date.now()}`;
    updateTab(activeTab, emptyTab());
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const renderContent = (content: string) => {
    if (renderMarkdown) return renderMarkdown(content);
    return <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{content}</pre>;
  };

  return (
    <div className={styles.overlay} role="dialog" aria-label="AI Assistant">
      {/* Header */}
      <div className={styles.header}>
        <Text weight="semibold">AI Assistant</Text>
        <Button
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          onClick={onClose}
          aria-label="Close"
        />
      </div>

      {/* Tabs — show only when there are multiple modes */}
      {modes.length > 1 && (
        <div className={styles.tabList}>
          <TabList
            selectedValue={activeTab}
            onTabSelect={(_, d) => setActiveTab(d.value as string)}
          >
            {modes.map((m) => (
              <Tab key={m.assistantId} value={m.assistantId}>
                {m.name}
              </Tab>
            ))}
          </TabList>
        </div>
      )}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          size="small"
          onClick={handleNewConversation}
          disabled={current.streaming}
        >
          New conversation
        </Button>
      </div>

      {/* Messages */}
      <div className={styles.messages} role="log" aria-live="polite">
        {current.messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: msg.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              className={`${styles.messageBubble} ${
                msg.role === "user" ? styles.userBubble : styles.assistantBubble
              }`}
            >
              {msg.role === "assistant"
                ? renderContent(msg.content || "\u200b")
                : <Text>{msg.content}</Text>}
            </div>

            {msg.role === "assistant" && msg.content && (
              <div className={styles.messageActions}>
                <Tooltip content="Copy" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<CopyRegular />}
                    onClick={() => handleCopy(msg.content)}
                    aria-label="Copy message"
                  />
                </Tooltip>
                <Tooltip content="Helpful" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbLikeRegular />}
                    onClick={() => handleReaction(msg, 1.0)}
                    aria-label="Thumbs up"
                  />
                </Tooltip>
                <Tooltip content="Not helpful" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbDislikeRegular />}
                    onClick={() => handleReaction(msg, 0.0)}
                    aria-label="Thumbs down"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        ))}

        {current.streaming &&
          current.messages[current.messages.length - 1]?.content === "" && (
          <Spinner size="tiny" label="Thinking…" />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.inputRow}>
        <Textarea
          style={{ flex: 1 }}
          placeholder={currentMode?.placeholder ?? "Ask a question…"}
          value={current.input}
          onChange={(_, d) => updateTab(activeTab, { input: d.value })}
          onKeyDown={handleKeyDown}
          disabled={current.streaming}
          rows={2}
          resize="none"
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => void handleSend()}
          disabled={!current.input.trim() || current.streaming}
          aria-label="Send"
        />
      </div>
    </div>
  );
}
